import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${BACKEND}/api`,
  withCredentials: true,
});

// Auto-refresh + redirect on 401. Skip for auth endpoints themselves so we
// don't loop when the user's password is simply wrong.
let refreshing = null;
api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || "";
    const isAuthEndpoint = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");

    if (status === 401 && !original._retry && !isAuthEndpoint) {
      original._retry = true;
      try {
        refreshing = refreshing || api.post("/auth/refresh");
        await refreshing;
        refreshing = null;
        return api(original);
      } catch (e) {
        refreshing = null;
        if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  },
);

export const fmtMoney = (amount, currency = "PKR") => {
  if (amount === null || amount === undefined || isNaN(amount)) return `${currency} 0.00`;
  return `${currency} ${Number(amount).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

export const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export function dimensionSummary(item) {
  const d = item.dimensions || {};
  switch (item.shape) {
    case "square": return d.side ? `${d.side}${d.height ? `×${d.height}` : ""} mm` : "";
    case "rectangle": return [d.length, d.width, d.height].filter(Boolean).join("×") + " mm";
    case "cylinder": return d.diameter ? `Ø${d.diameter}${d.length ? `×${d.length}` : ""} mm` : "";
    case "pipe": return d.outer_diameter ? `Ø${d.outer_diameter}×${d.wall_thickness || 0}t × ${d.length || 0} mm` : "";
    default: return "";
  }
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const STATUS_META = {
  quotation: { label: "Quotation", bg: "#F4F4F5", fg: "#52525B", bd: "#E4E4E7" },
  confirmed: { label: "Confirmed", bg: "#E0E7FF", fg: "#0020C2", bd: "#0020C2" },
  sourcing: { label: "Sourcing", bg: "#FEF08A", fg: "#854D0E", bd: "#EAB308" },
  ready: { label: "Ready", bg: "#D1FAE5", fg: "#065F46", bd: "#10B981" },
  in_transit_air: { label: "In Transit · Air", bg: "#FFEDD5", fg: "#9A3412", bd: "#F97316" },
  in_transit_sea: { label: "In Transit · Sea", bg: "#FFEDD5", fg: "#9A3412", bd: "#F97316" },
  in_transit_local: { label: "In Transit · Local", bg: "#FFEDD5", fg: "#9A3412", bd: "#F97316" },
  out_for_delivery: { label: "Out for Delivery", bg: "#FEE2E2", fg: "#B91C1C", bd: "#EF4444" },
  delivered: { label: "Delivered", bg: "#0A0A0A", fg: "#FFFFFF", bd: "#0A0A0A" },
  cancelled: { label: "Cancelled", bg: "#F4F4F5", fg: "#A1A1AA", bd: "#E4E4E7" },
};

export const STATUS_FLOW = [
  "quotation",
  "confirmed",
  "sourcing",
  "ready",
  "in_transit_air",
  "in_transit_sea",
  "in_transit_local",
  "out_for_delivery",
  "delivered",
];

export const PAYMENT_STATUS_META = {
  unpaid: { label: "Unpaid", bg: "#F4F4F5", fg: "#52525B", bd: "#E4E4E7" },
  partial: { label: "Partial", bg: "#FEF08A", fg: "#854D0E", bd: "#EAB308" },
  paid: { label: "Paid", bg: "#D1FAE5", fg: "#065F46", bd: "#10B981" },
  refund_due: { label: "Refund Due", bg: "#FEE2E2", fg: "#B91C1C", bd: "#EF4444" },
  cancelled_unpaid: { label: "Cancelled", bg: "#F4F4F5", fg: "#A1A1AA", bd: "#E4E4E7" },
};

export const ROLE_LABEL = {
  super_admin: "Super Admin",
  admin: "Admin",
  accounts: "Accounts",
  operations: "Operations",
  sales: "Sales",
};
